/** The case file a ticket was filed from, as the client's AI read it (`get_case`; backend
 *  `demo.lib.triage.cases`): the checks that ran, their findings most important first, and a
 *  count per severity.
 *
 *  A finding's title and explanation are ours (versioned templates); its evidence names the
 *  tenant's own references. The file is stored JSON, so every part is read defensively and shown
 *  as text — never markup. The whole file is also there as JSON, for what this view leaves out. */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import type { SupportTicketCase } from '@/api/supportTickets';
import { formatDate } from '@/utils/format';

type ChipColor = 'default' | 'info' | 'success' | 'warning' | 'error';

/** Most important first, as the case file orders its findings. */
const FINDING_SEVERITIES: { value: string; label: string; color: ChipColor }[] = [
  { value: 'explains', label: 'Explains', color: 'error' },
  { value: 'warning', label: 'Warning', color: 'warning' },
  { value: 'info', label: 'Info', color: 'info' },
  { value: 'ok', label: 'OK', color: 'success' },
];

const severityOf = (v: string) =>
  FINDING_SEVERITIES.find((s) => s.value === v) ?? {
    value: v,
    label: v,
    color: 'default' as const,
  };

interface Check {
  check: string;
  title: string;
  outcome: string;
  findings: number | null;
  note: string;
}

interface Evidence {
  type: string;
  id: string;
  ref: string;
  at: string;
}

interface Finding {
  check: string;
  code: string;
  severity: string;
  title: string;
  explanation: string;
  evidence: Evidence[];
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
const records = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? v.filter(isRecord) : [];

function readChecks(file: Record<string, unknown>): Check[] {
  return records(file.checks).map((c) => ({
    check: str(c.check),
    title: str(c.title),
    outcome: str(c.outcome),
    findings: typeof c.findings === 'number' ? c.findings : null,
    note: str(c.note),
  }));
}

function readFindings(file: Record<string, unknown>): Finding[] {
  return records(file.findings).map((f) => ({
    check: str(f.check),
    code: str(f.code),
    severity: str(f.severity),
    title: str(f.title),
    explanation: str(f.explanation),
    evidence: records(f.evidence).map((e) => ({
      type: str(e.type),
      id: str(e.id),
      ref: str(e.ref),
      at: str(e.at),
    })),
  }));
}

function evidenceText(e: Evidence): string {
  const name = [e.type, e.id && `#${e.id}`].filter(Boolean).join(' ');
  return [name, e.ref, e.at && formatDate(e.at)].filter(Boolean).join(' · ');
}

export function CaseFileView({ caseFile }: { caseFile: SupportTicketCase | null }) {
  if (!caseFile) {
    return (
      <Typography variant="body2" color="text.secondary" data-case-purged>
        The case file is gone: its retention ended and it was purged. The ticket keeps our title and
        the bookings it covered.
      </Typography>
    );
  }
  const file = isRecord(caseFile.file) ? caseFile.file : {};
  const checks = readChecks(file);
  const findings = readFindings(file);
  const summary = isRecord(file.summary) ? file.summary : {};

  return (
    <Stack spacing={2} data-case-file>
      <Typography variant="body2" color="text.secondary">
        Case {caseFile.id} · run {formatDate(caseFile.created_ts)} · on booking #
        {caseFile.subject_booking_id} ({caseFile.subject_scope})
      </Typography>

      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        aria-label="Findings by severity"
      >
        {FINDING_SEVERITIES.map((s) => (
          <Chip
            key={s.value}
            size="small"
            variant="outlined"
            color={typeof summary[s.value] === 'number' && summary[s.value] ? s.color : 'default'}
            label={`${s.label}: ${typeof summary[s.value] === 'number' ? summary[s.value] : 0}`}
          />
        ))}
      </Stack>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Findings
        </Typography>
        {findings.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No check found anything.
          </Typography>
        ) : (
          <Stack spacing={1.5} component="ol" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
            {findings.map((f, i) => {
              const sev = severityOf(f.severity);
              return (
                // Findings carry no id; their order is the file's.
                // eslint-disable-next-line react/no-array-index-key
                <Box component="li" key={i} data-finding={f.code}>
                  <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                    <Chip size="small" color={sev.color} label={sev.label} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {f.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {f.code}
                    </Typography>
                  </Stack>
                  {f.explanation && (
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {f.explanation}
                    </Typography>
                  )}
                  {f.evidence.length > 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ mt: 0.5 }}
                    >
                      Evidence: {f.evidence.map(evidenceText).join('; ')}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Checks that ran
        </Typography>
        <TableContainer>
          <Table size="small" aria-label="Checks that ran">
            <TableHead>
              <TableRow>
                <TableCell>Check</TableCell>
                <TableCell>Outcome</TableCell>
                <TableCell align="right">Findings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {checks.map((c) => (
                <TableRow key={c.check} data-check={c.check}>
                  <TableCell>
                    <Typography variant="body2">{c.title || c.check}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {c.check}
                    </Typography>
                    {c.note && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        {c.note}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={c.outcome === 'failed' ? 'error' : 'default'}
                      label={
                        c.outcome === 'failed' ? 'Failed' : c.outcome === 'ran' ? 'Ran' : c.outcome
                      }
                    />
                  </TableCell>
                  <TableCell align="right">{c.findings ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Accordion disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2">The case file as JSON</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            component="pre"
            sx={{
              m: 0,
              fontSize: 12,
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {JSON.stringify(file, null, 2)}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
